'use client';

// Share a forum thread to messaging apps / social networks.
// Explicit options for the apps our users actually use (WhatsApp, Viber,
// Telegram…) + copy link + the OS share sheet where available (mobile).

import { useRef, useState } from 'react';
import { Share2, Link2, Check, Ellipsis } from 'lucide-react';
import { useLang } from '@/lib/i18n/LanguageContext';

const MENU_WIDTH = 180;

const TARGETS: Array<{ key: string; label: string; color: string; href: (url: string, text: string) => string }> = [
  {
    key: 'whatsapp',
    label: 'WhatsApp',
    color: '#25d366',
    href: (url, text) => `https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`,
  },
  {
    key: 'viber',
    label: 'Viber',
    color: '#7360f2',
    href: (url, text) => `viber://forward?text=${encodeURIComponent(`${text} ${url}`)}`,
  },
  {
    key: 'telegram',
    label: 'Telegram',
    color: '#2aabee',
    href: (url, text) => `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
  },
  {
    key: 'x',
    label: 'X (Twitter)',
    color: '#e8ecf0',
    href: (url, text) => `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
  },
  {
    key: 'facebook',
    label: 'Facebook',
    color: '#1877f2',
    href: (url) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
];

export default function ShareMenu({ title }: { title: string }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  // Fixed positioning so ancestors with overflow-hidden can't clip the menu
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  function toggle() {
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({
        top: Math.min(r.bottom + 6, window.innerHeight - 280),
        left: Math.max(8, Math.min(r.left, window.innerWidth - MENU_WIDTH - 8)),
      });
    }
    setOpen((o) => !o);
  }

  // Read at click time — SSR has no window. UTM per channel so sign-ups can be
  // attributed to where the link was shared.
  const getUrl = (channel: string) => {
    const u = new URL(window.location.href);
    u.searchParams.set('utm_source', channel);
    u.searchParams.set('utm_medium', 'share');
    u.searchParams.set('utm_campaign', 'forum');
    return u.toString();
  };

  async function copyLink() {
    const url = getUrl('copy');
    let ok = false;
    try {
      await navigator.clipboard.writeText(url);
      ok = true;
    } catch {
      // Clipboard API can be blocked (permissions/iframe) — legacy fallback
      try {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand('copy');
        document.body.removeChild(ta);
      } catch {}
    }
    if (ok) {
      setCopied(true);
      setTimeout(() => { setCopied(false); setOpen(false); }, 1200);
    }
  }

  async function nativeShare() {
    try {
      await navigator.share({ title, url: getUrl('native') });
      setOpen(false);
    } catch {
      // user dismissed the sheet — nothing to do
    }
  }

  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  return (
    <div className="relative inline-block">
      <button
        ref={btnRef}
        onClick={toggle}
        className="flex items-center gap-1 text-xs"
        style={{ color: open ? 'var(--accent)' : 'var(--text-muted)' }}
      >
        <Share2 size={12} />
        {t('Share')}
      </button>

      {open && (
        <>
          {/* click-away backdrop */}
          <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
          <div
            className="fixed z-[61] rounded-xl py-1.5 animate-fade-in"
            style={{ top: pos.top, left: pos.left, width: MENU_WIDTH, background: '#1a2030', border: '1px solid #2c3547', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}
          >
            {TARGETS.map((tgt) => (
              <a
                key={tgt.key}
                href={tgt.href(getUrl(tgt.key), title)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setOpen(false)}
                className="flex items-center gap-2.5 px-3.5 py-2 text-xs font-medium transition-colors hover:bg-white/5"
                style={{ color: '#cbd5e1' }}
              >
                <span className="rounded-full shrink-0" style={{ width: 8, height: 8, background: tgt.color }} />
                {tgt.label}
              </a>
            ))}
            <div className="my-1" style={{ borderTop: '1px solid #2c3547' }} />
            <button
              onClick={copyLink}
              className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs font-medium transition-colors hover:bg-white/5"
              style={{ color: copied ? '#22c55e' : '#cbd5e1' }}
            >
              {copied ? <Check size={12} /> : <Link2 size={12} />}
              {copied ? t('Copied!') : t('Copy link')}
            </button>
            {canNativeShare && (
              <button
                onClick={nativeShare}
                className="w-full flex items-center gap-2.5 px-3.5 py-2 text-xs font-medium transition-colors hover:bg-white/5"
                style={{ color: '#cbd5e1' }}
              >
                <Ellipsis size={12} />
                {t('More options…')}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
